import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Truck,
  ArrowLeft,
  ExternalLink,
  Package,
  CheckCircle2,
  Copy,
  Info,
  MapPin,
  Search,
} from "lucide-react";
import { getOrderTracking } from "../api/orderApi";

// ─── helpers ────────────────────────────────────────────────────────────────
const formatOrderId = (id) => {
  if (id && id.length === 24) return `ORD-${id.slice(-6).toUpperCase()}`;
  return id || "N/A";
};

const STATUS_META = {
  SHIPPED: {
    label: "Shipped",
    color: "bg-blue-100 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
    dot: "bg-blue-500",
  },
  DELIVERED: {
    label: "Delivered",
    color: "bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
    dot: "bg-green-500",
  },
  APPROVED: {
    label: "Approved",
    color: "bg-indigo-100 text-indigo-700 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800",
    dot: "bg-indigo-500",
  },
  PENDING: {
    label: "Pending",
    color: "bg-orange-100 text-orange-700 border border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800",
    dot: "bg-orange-500",
  },
  CANCELLED: {
    label: "Cancelled",
    color: "bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800",
    dot: "bg-red-500",
  },
};

// ─── How To Track steps ──────────────────────────────────────────────────────
const HOW_TO_TRACK_STEPS = [
  {
    step: 1,
    icon: <Copy size={18} />,
    title: "Copy your AWB Code",
    desc: "Your AWB number is shown on the left. Click the copy button next to it.",
  },
  {
    step: 2,
    icon: <ExternalLink size={18} />,
    title: "Visit Shiprocket",
    desc: 'Click the "Track on Shiprocket" button to open the official tracking portal.',
  },
  {
    step: 3,
    icon: <Search size={18} />,
    title: "Paste & Search",
    desc: "Paste your AWB number in the tracking field and click Track.",
  },
  {
    step: 4,
    icon: <MapPin size={18} />,
    title: "View Live Location",
    desc: "See real-time status updates and the estimated delivery date.",
  },
];

// ─── Component ───────────────────────────────────────────────────────────────
const TrackOrder = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // ── Fetch order
  useEffect(() => {
    if (!orderId) return;
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const res = await getOrderTracking(orderId);
        if (res.success && res.data?.order) {
          setOrder(res.data.order);
        } else {
          setError("Order not found.");
        }
      } catch (err) {
        console.error("Error fetching tracking:", err);
        if (err.response?.status === 403) {
          setError("You are not authorised to view this order.");
        } else if (err.response?.status === 404) {
          setError("Order not found.");
        } else {
          setError("Failed to load tracking information. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  // ── Copy AWB to clipboard
  const handleCopyAWB = () => {
    const awb = order?.trackingData?.trackingId;
    if (!awb) return;
    navigator.clipboard.writeText(awb).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-14 h-14 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">Loading tracking info…</p>
        </div>
      </div>
    );
  }

  // ── Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="text-center max-w-md space-y-4">
          <div className="w-20 h-20 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto">
            <Package size={36} className="text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Something went wrong</h2>
          <p className="text-gray-500 dark:text-gray-400">{error}</p>
          <button
            onClick={() => navigate("/profile")}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-xl font-semibold text-sm hover:opacity-80 transition"
          >
            <ArrowLeft size={16} /> Back to Profile
          </button>
        </div>
      </div>
    );
  }

  const tracking = order?.trackingData;
  const hasTracking = !!(tracking?.trackingId || tracking?.courierName);
  const statusMeta = STATUS_META[order?.status] || STATUS_META["PENDING"];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-4 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">

        {/* ── Back navigation */}
        <Link
          to="/profile"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-3"
        >
          <ArrowLeft size={16} /> Back to My Orders
        </Link>

        {/* ── Header row */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-5">
          <div>
            <h1 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">
              Track Order
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Order {formatOrderId(orderId)} • Placed on {order?.createdAt ? new Date(order.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "—"}
            </p>
          </div>
          <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${statusMeta.color}`}>
            <span className={`w-2 h-2 rounded-full ${statusMeta.dot}`} />
            {statusMeta.label}
          </span>
        </div>

        {/* ── Main Layout: dynamic based on whether tracking data exists */}
        {hasTracking ? (
          /* TWO COLUMN: Tracking Details (left) + How to Track (right) */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* LEFT: Tracking Details */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                <div className="p-5 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Truck className="text-blue-500" size={20} />
                    Tracking Details
                  </h2>
                </div>
                <div className="p-6 space-y-6">
                  {/* Courier & AWB Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {tracking.courierName && (
                      <div className="bg-gray-50 dark:bg-gray-900/50 p-5 rounded-xl border border-gray-100 dark:border-gray-700">
                        <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1">
                          Courier Partner
                        </p>
                        <p className="text-xl font-black text-gray-900 dark:text-white">
                          {tracking.courierName}
                        </p>
                      </div>
                    )}
                    {tracking.trackingId && (
                      <div className="bg-gray-50 dark:bg-gray-900/50 p-5 rounded-xl border border-gray-100 dark:border-gray-700">
                        <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1">
                          AWB Number
                        </p>
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xl font-black text-blue-600 dark:text-blue-400 font-mono">
                            {tracking.trackingId}
                          </p>
                          <button
                            onClick={handleCopyAWB}
                            className={`p-2 rounded-lg transition-all ${
                              copied
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 shadow-sm border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                            }`}
                            title="Copy AWB"
                          >
                            {copied ? <CheckCircle2 size={18} /> : <Copy size={18} />}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                    <a
                      href="https://www.shiprocket.in/shipment-tracking/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full md:w-auto md:inline-flex px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-blue-500/25 active:scale-[0.98]"
                    >
                      Track on Shiprocket <ExternalLink size={16} />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: How to Track */}
            <div className="lg:col-span-1">
              <HowToTrackCard />
            </div>
          </div>
        ) : (
          /* NO TRACKING DATA: show status-aware notice + How to Track */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Status-aware info notice */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 flex flex-col items-center text-center gap-4">

                {order?.status === "DELIVERED" ? (
                  /* ── DELIVERED: no tracking data saved, but order is done */
                  <>
                    <div className="w-16 h-16 bg-green-50 dark:bg-green-900/20 rounded-full flex items-center justify-center border border-green-100 dark:border-green-800">
                      <CheckCircle2 size={28} className="text-green-500" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      Order Delivered
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-sm">
                      Your order has been successfully delivered. Tracking information is no longer available for completed orders.
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-full text-sm text-green-700 dark:text-green-400 font-medium">
                      <CheckCircle2 size={14} />
                      Delivered successfully
                    </div>
                  </>
                ) : order?.status === "CANCELLED" ? (
                  /* ── CANCELLED */
                  <>
                    <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center border border-red-100 dark:border-red-800">
                      <Package size={28} className="text-red-400" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      Order Cancelled
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-sm">
                      This order was cancelled. No shipment was created for this order.
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-full text-sm text-red-600 dark:text-red-400 font-medium">
                      <span className="w-2 h-2 rounded-full bg-red-400" />
                      Cancelled
                    </div>
                  </>
                ) : (
                  /* ── PENDING / APPROVED: waiting for shipment */
                  <>
                    <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center border border-blue-100 dark:border-blue-800">
                      <Package size={28} className="text-blue-400" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      Tracking details will appear here
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-sm">
                      Your order is confirmed and being processed. Once your package is shipped, the AWB code and courier name will show up here automatically.
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-full text-sm text-gray-600 dark:text-gray-300 font-medium">
                      <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                      Waiting for shipment
                    </div>
                  </>
                )}

              </div>
            </div>

            {/* How to Track on right */}
            <div className="lg:col-span-1">
              <HowToTrackCard />
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

// ── Extracted How to Track card to avoid duplication
const HowToTrackCard = () => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 h-full">
    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
      <Info className="text-blue-500" size={20} />
      How to Track
    </h2>
    <div className="relative">
      <div className="absolute left-[19px] top-4 bottom-4 w-[2px] bg-gray-100 dark:bg-gray-700" />
      <div className="space-y-6 relative">
        {HOW_TO_TRACK_STEPS.map((item) => (
          <div key={item.step} className="flex gap-4 relative z-10">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white dark:bg-gray-800 border-2 border-blue-100 dark:border-blue-800 text-blue-600 dark:text-blue-400 flex items-center justify-center shadow-sm">
              {item.icon}
            </div>
            <div className="pt-2">
              <p className="text-sm font-bold text-gray-900 dark:text-white mb-1">
                {item.step}. {item.title}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {item.desc}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default TrackOrder;

